import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'react-hot-toast';
import { Plus, Trash2, Save } from 'lucide-react';
import { rbacApi } from '../../services/rbacApi';

/**
 * Role Permissions Manager — pick a DocType, then grant rights per role.
 * Mirrors ERPNext: rows are (doctype x role) with read/write/create/... flags.
 */
const PermissionsTab = () => {
  const [catalog, setCatalog] = useState([]);
  const [rights, setRights] = useState([]);
  const [roles, setRoles] = useState([]);
  const [doctype, setDoctype] = useState('');
  const [rows, setRows] = useState([]);
  const [addRole, setAddRole] = useState('');
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    rbacApi.doctypeCatalog().then((r) => setCatalog(r.data)).catch(() => toast.error('Failed to load doctypes'));
    rbacApi.rights().then((r) => setRights(r.data));
    rbacApi.listRoles().then((r) => setRoles(r.data));
  }, []);

  const loadRows = useCallback((dt) => {
    rbacApi.listPermissions({ doctype: dt }).then((r) => setRows(r.data)).catch(() => toast.error('Failed to load permissions'));
  }, []);

  const onPickDoctype = (dt) => { setDoctype(dt); setAddRole(''); loadRows(dt); };

  const applyStarter = async () => {
    if (!window.confirm('Apply the SAP starter authorization profile to the standard roles (Sales, Purchase, Stock, HR, Accounts)? Existing grants are kept; this only adds the default ones.')) return;
    setApplying(true);
    try {
      const r = await rbacApi.applyStarterProfile(false);
      toast.success(`Starter profile applied (${r.data.applied} grants added/updated)`);
      if (doctype) loadRows(doctype);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply starter profile');
    } finally { setApplying(false); }
  };

  const addRow = () => {
    if (!addRole) { toast.error('Select a role to add'); return; }
    if (rows.some((r) => r.role === addRole)) { toast.error('Role already in the table'); return; }
    setRows((prev) => [...prev, { doctype, role: addRole, read: true, write: false, create: false, delete: false, submit: false, cancel: false, print: false, export: false, share: false, _new: true }]);
    setAddRole('');
  };

  const toggle = (idx, right) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [right]: !r[right] } : r)));

  const saveRow = async (row) => {
    try {
      const payload = { doctype, role: row.role };
      rights.forEach((r) => { payload[r] = !!row[r]; });
      await rbacApi.upsertPermission(payload);
      toast.success(`Saved permissions for ${row.role}`);
      loadRows(doctype);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const deleteRow = async (row) => {
    if (row._new) { setRows((prev) => prev.filter((r) => r.role !== row.role)); return; }
    try { await rbacApi.deletePermission(row.permission_id); toast.success('Removed'); loadRows(doctype); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div data-testid="permissions-tab">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4 text-sm text-orange-800 flex items-start justify-between gap-4">
        <span>
          Pick a DocType and grant rights per role. Access is <strong>deny-by-default</strong>: a non-superuser
          can create / edit / delete only where their role is explicitly granted. Reads stay open. System
          Manager always has full access.
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={applyStarter}
          disabled={applying}
          data-testid="apply-starter-profile-btn"
          className="shrink-0 border-orange-300 text-orange-800 hover:bg-orange-100"
        >
          {applying ? 'Applying…' : 'Apply SAP starter profile'}
        </Button>
      </div>

      <div className="max-w-md mb-5">
        <Label>DocType</Label>
        <Select value={doctype} onValueChange={onPickDoctype}>
          <SelectTrigger data-testid="perm-doctype-select"><SelectValue placeholder="Select a DocType to govern" /></SelectTrigger>
          <SelectContent className="max-h-80">
            {catalog.map((group) => (
              <SelectGroup key={group.module}>
                <SelectLabel>{group.module}</SelectLabel>
                {group.items.map((it) => (
                  <SelectItem key={it.key} value={it.key}>{it.label}</SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
      </div>

      {doctype && (
        <>
          <div className="flex items-end gap-2 mb-3">
            <div className="w-64">
              <Label>Add role</Label>
              <Select value={addRole} onValueChange={setAddRole}>
                <SelectTrigger data-testid="perm-add-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => <SelectItem key={r.role_id} value={r.name}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={addRow} data-testid="perm-add-row-btn"><Plus className="w-4 h-4 mr-1" />Add</Button>
          </div>

          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left">Role</th>
                  {rights.map((r) => <th key={r} className="px-2 py-2 text-center capitalize">{r}</th>)}
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.length === 0 && (
                  <tr><td colSpan={rights.length + 2} className="px-3 py-6 text-center text-gray-400">No permission rows yet — no role can create/edit/delete this DocType (deny-by-default). Add a role to grant access.</td></tr>
                )}
                {rows.map((row, idx) => (
                  <tr key={row.role} className="hover:bg-gray-50" data-testid={`perm-row-${row.role}`}>
                    <td className="px-3 py-2 font-medium whitespace-nowrap">{row.role}</td>
                    {rights.map((r) => (
                      <td key={r} className="px-2 py-2 text-center">
                        <Checkbox checked={!!row[r]} onCheckedChange={() => toggle(idx, r)} data-testid={`perm-${row.role}-${r}`} />
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right whitespace-nowrap space-x-1">
                      <Button size="sm" className="bg-orange-600 hover:bg-orange-700 h-7" onClick={() => saveRow(row)} data-testid={`perm-save-${row.role}`}>
                        <Save className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600 h-7" onClick={() => deleteRow(row)} data-testid={`perm-delete-${row.role}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default PermissionsTab;
